import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import googleGeoCode from '../utils/GoogleGeoCode';
import { mobileTrayViews } from './mobile/mobile-utils';
import { fetchMapsData } from '../LocationInput/fetch-maps-data';

export const SEARCH_CONTEXT = createContext({});
export const useSearch = () => useContext(SEARCH_CONTEXT);

export const SearchContextProvider = (props) => {
  const {
    onSearch,
    onFocus,
    onBlur,
    searchFilters,
    children,
    currentLocale,
    origin,
    defaultLocation,
    defaultActivity,
    intl,
    displaySearchWidget,
    closeSearchWidgetFn
  } = props;

  const [activityValue, setActivityValue] = useState();
  const [locationValue, setLocationValue] = useState();
  const [fetchingGeoData, setFetchingGeoData] = useState(false);
  const [activeInput, setActiveInput] = useState();
  const [mobileTrayViewValue, setMobileTrayView] = useState('main');
  const [defaultActivityValue, setDefaultActivityValue] = useState();
  const [defaultLocationValue, setDefaultLocationValue] = useState();

  // store validator methods from child input components
  const validateActivity = useRef();
  const setValidateActivity = useCallback((validator) => {
    validateActivity.current = validator;
  }, []);

  const validateLocation = useRef();
  const setValidateLocation = useCallback((validator) => {
    validateLocation.current = validator;
  }, []);

  const fetchGeoCode = async (selectedMapSuggestion) => {
    setFetchingGeoData(true);
    const label = get(selectedMapSuggestion || locationValue, 'label');
    const placeId = get(selectedMapSuggestion || locationValue, 'value.place_id');
    const { queryObj, latitude, longitude } = await googleGeoCode(
      placeId,
      label
    );
    return { label, query: queryObj, latitude, longitude };
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const activityValid = validateActivity.current();
      if (!activityValid) {
        return;
      }
      if (!locationValue || isEmpty(locationValue)) return;
      let locationData;
      let selectedMapSuggestion;
      try {
        if ('string' === typeof locationValue) {
          const mapSuggestions = await fetchMapsData(locationValue);
          selectedMapSuggestion = mapSuggestions[0];
        }
        locationData = await fetchGeoCode(selectedMapSuggestion);
      } catch (e) {
        setFetchingGeoData(false);
        console.error('LocationInput.onChange', e);
        return;
      }
      const hasValue = !(
        isEmpty(locationData) ||
        isEmpty(locationData.query) ||
        !locationData.query.location
      );
      if (!hasValue) {
        setFetchingGeoData(false);
        return;
      }
      // build filter query from searchFilters prop
      const filterQuery =
        searchFilters && searchFilters.filterText && searchFilters.filterType
          ? `&${searchFilters.filterType}=${searchFilters.filterText}`
          : '';

      if ('function' === typeof onSearch) {
        onSearch({
          activity: activityValue,
          location: locationData,
          filterQuery,
          currentLocale
        });
      }
    },
    [
      validateActivity.current,
      validateLocation.current,
      activityValue,
      locationValue
    ]
  );

  useEffect(() => {
    if (activeInput) {
      if ('function' === typeof onFocus) {
        onFocus(activeInput);
      }
    } else if ('function' === typeof onBlur) {
      onBlur();
    }
  }, [activeInput]);

  useEffect(() => {
    if (!displaySearchWidget) {
      setMobileTrayView('main');
    }
  }, [displaySearchWidget]);

  const activityValueChanged = (value, focusNext = true, isInterimOption) => {
    if ((!defaultActivityValue || !isInterimOption) && !isEmpty(value)) {
      setDefaultActivityValue(value);
    }
    setActivityValue(value);
    // focus location input if new activity is selected and no location exists
    if (focusNext && value?.value && !locationValue?.query) {
      setActiveInput('location');
    }
  };

  const locationValueChanged = (value, focusNext = true, isInterimOption) => {
    if ((!defaultLocationValue || !isInterimOption) && !isEmpty(value)) {
      setDefaultLocationValue(value);
    }
    setLocationValue(value);
    // focus activity input if new location is selected and no activity exists
    if (focusNext && value?.query && !activityValue?.value) {
      setActiveInput('activity');
    }
  };

  const getMobileTrayView = () => {
    return mobileTrayViews({ intl })[mobileTrayViewValue];
  };

  const providerValue = useMemo(() => {
    return {
      activityValue,
      setActivityValue: activityValueChanged,
      locationValue,
      setLocationValue: locationValueChanged,
      activeInput,
      setActiveInput,
      validateActivity,
      setValidateActivity,
      validateLocation,
      setValidateLocation,
      fetchingGeoData,
      handleSubmit,
      origin,
      defaultActivity,
      defaultActivityValue,
      defaultLocation,
      defaultLocationValue,
      intl,
      displaySearchWidget,
      closeSearchWidgetFn,
      mobileTrayViewValue,
      getMobileTrayView,
      setMobileTrayView
    };
  }, [
    activityValue,
    locationValue,
    activeInput,
    setValidateActivity,
    setValidateLocation,
    handleSubmit,
    defaultActivity,
    defaultLocation,
    fetchingGeoData,
    intl,
    mobileTrayViewValue,
    displaySearchWidget
  ]);

  return (
    <SEARCH_CONTEXT.Provider value={providerValue}>
      {children}
    </SEARCH_CONTEXT.Provider>
  );
};
